export const CANVAS_RUNTIME_VERSION = 1;

const STORAGE_PREFIX = "originCanvas.runtime.v1";
const MAX_STORED_CHARS = 4_096;
const MAX_CONTROLS = 12;
const MAX_OPTIONS = 32;
const MAX_RUNTIME_CELLS = 20;
const MAX_BINDINGS_PER_CELL = 12;
const MAX_DISCLOSURES = 12;
const MAX_BANDS = 12;
const ID_RE = /^[a-z][a-z0-9_-]{0,63}$/;
const SORT_VALUES = new Set(["asc", "desc"]);
const CONTROL_KINDS = new Set(["range", "categorical", "sort"]);
const BINDING_KINDS = new Set(["parameter", "filter", "sort"]);
const FOCUS_KINDS = new Set(["filter"]);
const DISCLOSURE_KINDS = new Set(["coverage", "estimate", "limitation", "method"]);
const RUNTIME_KEYS = new Set(["version", "controls", "cells", "bands", "disclosures"]);
const CONTROL_KEYS = new Set(["id", "kind", "label", "defaultValue", "options"]);
const OPTION_KEYS = new Set(["value", "label", "clear"]);
const CELL_KEYS = new Set([
    "id",
    "kernelId",
    "bindings",
    "focus",
    "disclosures",
    "minimumWidth",
]);
const BINDING_KEYS = new Set(["controlId", "kind", "parameter", "field"]);
const FOCUS_KEYS = new Set(["kind", "field"]);
const BAND_KEYS = new Set(["id", "title", "startsAtCellId", "defaultOpen", "description"]);
const DISCLOSURE_KEYS = new Set(["id", "kind", "text", "label"]);

function isRecord(value) {
    return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function cleanText(value, maximum = 160) {
    if (typeof value !== "string") return null;
    const text = value.trim();
    return text && text.length <= maximum ? text : null;
}

function cleanReference(value, maximum = 128) {
    if (typeof value !== "string") return null;
    const text = value.trim();
    return text && text.length <= maximum ? text : null;
}

function cleanId(value) {
    if (typeof value !== "string") return null;
    const id = value.trim();
    return id && ID_RE.test(id) ? id : null;
}

export function isCanvasRuntimeScalar(value) {
    return (typeof value === "string" && value.length <= 256)
        || typeof value === "boolean"
        || (typeof value === "number" && Number.isFinite(value));
}

function scalarKey(value) {
    return `${typeof value}:${String(value)}`;
}

function sameScalar(left, right) {
    return typeof left === typeof right && Object.is(left, right);
}

function issue(kind, code, message, path = null) {
    return { kind, code, message, path };
}

function reportUnknownKeys(raw, allowed, path, issues) {
    for (const key of Object.keys(raw)) {
        if (allowed.has(key)) continue;
        issues.push(issue(
            "unsupported",
            "unsupported-runtime-field",
            `Canvas runtime field ${path}.${key} is not supported.`,
            `${path}.${key}`,
        ));
    }
}

function normalizeDisclosure(raw, path, issues) {
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-disclosure", "A disclosure must be an object.", path));
        return null;
    }
    reportUnknownKeys(raw, DISCLOSURE_KEYS, path, issues);
    const id = cleanId(raw.id);
    const text = cleanText(raw.text, 600);
    const label = cleanText(raw.label, 80);
    if (!id || !text) {
        issues.push(issue(
            "conflict",
            "invalid-disclosure",
            "A disclosure requires a stable id and non-blank text.",
            path,
        ));
        return null;
    }
    if (!DISCLOSURE_KINDS.has(raw.kind)) {
        issues.push(issue(
            "unsupported",
            "unsupported-disclosure-kind",
            `Disclosure ${id} uses unsupported kind ${String(raw.kind || "(missing)")}.`,
            path,
        ));
        return null;
    }
    return { id, kind: raw.kind, text, ...(label ? { label } : {}) };
}

function normalizeDisclosures(raw, path, issues) {
    if (raw == null) return [];
    if (!Array.isArray(raw)) {
        issues.push(issue("conflict", "invalid-disclosures", "Disclosures must be an array.", path));
        return [];
    }
    if (raw.length > MAX_DISCLOSURES) {
        issues.push(issue(
            "conflict",
            "too-many-disclosures",
            `At most ${MAX_DISCLOSURES} disclosures are supported.`,
            path,
        ));
    }
    const seen = new Set();
    const result = [];
    raw.slice(0, MAX_DISCLOSURES).forEach((value, index) => {
        const disclosure = normalizeDisclosure(value, `${path}[${index}]`, issues);
        if (!disclosure) return;
        if (seen.has(disclosure.id)) {
            issues.push(issue(
                "conflict",
                "duplicate-disclosure-id",
                `Disclosure id ${disclosure.id} is duplicated.`,
                `${path}[${index}]`,
            ));
            return;
        }
        seen.add(disclosure.id);
        result.push(disclosure);
    });
    return result;
}

function normalizeOptions(raw, controlId, path, issues) {
    if (!Array.isArray(raw) || raw.length === 0 || raw.length > MAX_OPTIONS) {
        issues.push(issue(
            "conflict",
            "invalid-control-options",
            `Control ${controlId} requires 1-${MAX_OPTIONS} options.`,
            path,
        ));
        return [];
    }
    const values = new Set();
    let clearCount = 0;
    const options = [];
    raw.forEach((rawOption, index) => {
        const optionPath = `${path}[${index}]`;
        if (!isRecord(rawOption) || !isCanvasRuntimeScalar(rawOption.value)) {
            issues.push(issue(
                "conflict",
                "invalid-control-option",
                `Control ${controlId} has an option without a scalar value.`,
                optionPath,
            ));
            return;
        }
        reportUnknownKeys(rawOption, OPTION_KEYS, optionPath, issues);
        const label = cleanText(rawOption.label, 80);
        if (!label) {
            issues.push(issue(
                "conflict",
                "invalid-control-option",
                `Control ${controlId} has an option without a label.`,
                optionPath,
            ));
            return;
        }
        if (rawOption.clear != null && typeof rawOption.clear !== "boolean") {
            issues.push(issue(
                "conflict",
                "invalid-control-option",
                `Control ${controlId} option clear must be boolean when present.`,
                optionPath,
            ));
            return;
        }
        const key = scalarKey(rawOption.value);
        if (values.has(key)) {
            issues.push(issue(
                "conflict",
                "duplicate-control-option",
                `Control ${controlId} repeats option value ${String(rawOption.value)}.`,
                optionPath,
            ));
            return;
        }
        values.add(key);
        const clear = rawOption.clear === true;
        if (clear) clearCount += 1;
        options.push({ value: rawOption.value, label, ...(clear ? { clear: true } : {}) });
    });
    if (clearCount > 1) {
        issues.push(issue(
            "conflict",
            "multiple-clear-options",
            `Control ${controlId} may declare only one clear option.`,
            path,
        ));
    }
    return options;
}

function normalizeControl(raw, path, issues) {
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-control", "A control must be an object.", path));
        return null;
    }
    reportUnknownKeys(raw, CONTROL_KEYS, path, issues);
    const id = cleanId(raw.id);
    const label = cleanText(raw.label, 80);
    if (!id || !label) {
        issues.push(issue(
            "conflict",
            "invalid-control",
            "A control requires a stable id and non-blank label.",
            path,
        ));
        return null;
    }
    if (!CONTROL_KINDS.has(raw.kind)) {
        issues.push(issue(
            "unsupported",
            "unsupported-control-kind",
            `Control ${id} uses unsupported kind ${String(raw.kind || "(missing)")}.`,
            path,
        ));
        return null;
    }
    if (raw.kind === "sort") {
        if (raw.options != null) {
            issues.push(issue(
                "unsupported",
                "unsupported-sort-options",
                `Sort control ${id} uses fixed ascending and descending options.`,
                `${path}.options`,
            ));
        }
        const defaultValue = Object.prototype.hasOwnProperty.call(raw, "defaultValue")
            ? raw.defaultValue
            : "desc";
        if (!SORT_VALUES.has(defaultValue)) {
            issues.push(issue(
                "conflict",
                "invalid-control-default",
                `Sort control ${id} must default to asc or desc.`,
                path,
            ));
            return null;
        }
        return {
            id,
            kind: "sort",
            label,
            defaultValue,
            options: [
                { value: "desc", label: "Descending" },
                { value: "asc", label: "Ascending" },
            ],
        };
    }
    const options = normalizeOptions(raw.options, id, `${path}.options`, issues);
    if (!options.length) return null;
    if (raw.kind === "range" && options.some((option) => typeof option.value !== "number")) {
        issues.push(issue(
            "conflict",
            "invalid-range-option",
            `Range control ${id} requires numeric option values.`,
            `${path}.options`,
        ));
        return null;
    }
    if (raw.kind === "range" && options.some((option) => option.clear)) {
        issues.push(issue(
            "conflict",
            "invalid-range-option",
            `Range control ${id} cannot declare a clear option.`,
            `${path}.options`,
        ));
        return null;
    }
    const defaultValue = Object.prototype.hasOwnProperty.call(raw, "defaultValue")
        ? raw.defaultValue
        : options[0].value;
    if (!options.some((option) => sameScalar(option.value, defaultValue))) {
        issues.push(issue(
            "conflict",
            "invalid-control-default",
            `Control ${id} defaultValue must match one declared option.`,
            path,
        ));
        return null;
    }
    return { id, kind: raw.kind, label, defaultValue, options };
}

function normalizeBinding(raw, cellId, controls, path, issues) {
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-binding", `Cell ${cellId} has an invalid binding.`, path));
        return null;
    }
    reportUnknownKeys(raw, BINDING_KEYS, path, issues);
    const controlId = cleanId(raw.controlId);
    if (!controlId || !controls.has(controlId)) {
        issues.push(issue(
            "conflict",
            "unknown-binding-control",
            `Cell ${cellId} references an unknown control.`,
            path,
        ));
        return null;
    }
    if (!BINDING_KINDS.has(raw.kind)) {
        issues.push(issue(
            "unsupported",
            "unsupported-binding-kind",
            `Cell ${cellId} uses unsupported binding kind ${String(raw.kind || "(missing)")}.`,
            path,
        ));
        return null;
    }
    const control = controls.get(controlId);
    if (raw.kind === "sort") {
        const field = cleanReference(raw.field);
        if (control.kind !== "sort" || !field || raw.parameter != null) {
            issues.push(issue(
                "conflict",
                "incompatible-binding",
                `Cell ${cellId} sort binding requires a sort control and field.`,
                path,
            ));
            return null;
        }
        return { controlId, kind: "sort", field };
    }
    if (raw.kind === "filter") {
        const field = cleanReference(raw.field);
        if (control.kind !== "categorical" || !field || raw.parameter != null) {
            issues.push(issue(
                "conflict",
                "incompatible-binding",
                `Cell ${cellId} filter binding requires a categorical control and field.`,
                path,
            ));
            return null;
        }
        return { controlId, kind: "filter", field };
    }
    const parameter = cleanId(raw.parameter);
    if (control.kind === "sort" || !parameter || raw.field != null) {
        issues.push(issue(
            "conflict",
            "incompatible-binding",
            `Cell ${cellId} parameter binding requires a range or categorical control and parameter.`,
            path,
        ));
        return null;
    }
    return { controlId, kind: "parameter", parameter };
}

function normalizeFocusBinding(raw, cellId, path, issues) {
    if (!isRecord(raw) || !FOCUS_KINDS.has(raw.kind)) {
        issues.push(issue(
            "unsupported",
            "unsupported-focus-binding",
            `Cell ${cellId} uses an unsupported focus binding.`,
            path,
        ));
        return null;
    }
    reportUnknownKeys(raw, FOCUS_KEYS, path, issues);
    const field = cleanReference(raw.field);
    if (!field) {
        issues.push(issue(
            "conflict",
            "invalid-focus-binding",
            `Cell ${cellId} focus binding requires a field.`,
            path,
        ));
        return null;
    }
    return { kind: "filter", field };
}

function normalizeRuntimeCell(raw, controls, path, issues) {
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-runtime-cell", "A runtime cell must be an object.", path));
        return null;
    }
    reportUnknownKeys(raw, CELL_KEYS, path, issues);
    const id = cleanId(raw.id);
    const kernelId = cleanReference(raw.kernelId);
    if (!id || !kernelId) {
        issues.push(issue(
            "conflict",
            "invalid-runtime-cell",
            "A runtime cell requires a stable id and kernelId.",
            path,
        ));
        return null;
    }
    const bindingValues = raw.bindings == null ? [] : raw.bindings;
    if (!Array.isArray(bindingValues) || bindingValues.length > MAX_BINDINGS_PER_CELL) {
        issues.push(issue(
            "conflict",
            "invalid-bindings",
            `Cell ${id} supports at most ${MAX_BINDINGS_PER_CELL} bindings.`,
            `${path}.bindings`,
        ));
    }
    const bindings = [];
    const bindingControls = new Set();
    const bindingTargets = new Set();
    (Array.isArray(bindingValues) ? bindingValues.slice(0, MAX_BINDINGS_PER_CELL) : [])
        .forEach((value, index) => {
            const binding = normalizeBinding(value, id, controls, `${path}.bindings[${index}]`, issues);
            if (!binding) return;
            const target = binding.kind === "parameter"
                ? `parameter:${binding.parameter}`
                : `${binding.kind}:${binding.field}`;
            if (bindingControls.has(binding.controlId) || bindingTargets.has(target)) {
                issues.push(issue(
                    "conflict",
                    "duplicate-binding",
                    `Cell ${id} repeats a control or target binding.`,
                    `${path}.bindings[${index}]`,
                ));
                return;
            }
            bindingControls.add(binding.controlId);
            bindingTargets.add(target);
            bindings.push(binding);
        });
    const focusValues = raw.focus == null ? [] : raw.focus;
    if (!Array.isArray(focusValues) || focusValues.length > MAX_BINDINGS_PER_CELL) {
        issues.push(issue(
            "conflict",
            "invalid-focus",
            `Cell ${id} supports at most ${MAX_BINDINGS_PER_CELL} focus bindings.`,
            `${path}.focus`,
        ));
    }
    const focus = [];
    const focusFields = new Set();
    (Array.isArray(focusValues) ? focusValues.slice(0, MAX_BINDINGS_PER_CELL) : [])
        .forEach((value, index) => {
            const binding = normalizeFocusBinding(value, id, `${path}.focus[${index}]`, issues);
            if (!binding) return;
            if (focusFields.has(binding.field)) {
                issues.push(issue(
                    "conflict",
                    "duplicate-focus-binding",
                    `Cell ${id} repeats focus field ${binding.field}.`,
                    `${path}.focus[${index}]`,
                ));
                return;
            }
            focusFields.add(binding.field);
            focus.push(binding);
        });
    const minimumWidth = raw.minimumWidth == null ? null : raw.minimumWidth;
    if (raw.minimumWidth != null
        && (typeof minimumWidth !== "number"
            || !Number.isFinite(minimumWidth)
            || minimumWidth < 160
            || minimumWidth > 1_200)) {
        issues.push(issue(
            "conflict",
            "invalid-minimum-width",
            `Cell ${id} minimumWidth must be between 160 and 1200 pixels.`,
            `${path}.minimumWidth`,
        ));
    }
    return {
        id,
        kernelId,
        bindings,
        focus,
        disclosures: normalizeDisclosures(raw.disclosures, `${path}.disclosures`, issues),
        ...(Number.isFinite(minimumWidth) && minimumWidth >= 160 && minimumWidth <= 1_200
            ? { minimumWidth }
            : {}),
    };
}

function normalizeBand(raw, cellIds, path, issues) {
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-band", "A band must be an object.", path));
        return null;
    }
    reportUnknownKeys(raw, BAND_KEYS, path, issues);
    const id = cleanId(raw.id);
    const title = cleanText(raw.title, 100);
    const startsAtCellId = cleanId(raw.startsAtCellId);
    if (!id || !title || !startsAtCellId || !cellIds.has(startsAtCellId)) {
        issues.push(issue(
            "conflict",
            "invalid-band",
            "A band requires a stable id, title, and known startsAtCellId.",
            path,
        ));
        return null;
    }
    const description = cleanText(raw.description, 240);
    if (raw.defaultOpen != null && typeof raw.defaultOpen !== "boolean") {
        issues.push(issue(
            "conflict",
            "invalid-band-default",
            `Band ${id} defaultOpen must be boolean when present.`,
            `${path}.defaultOpen`,
        ));
    }
    return {
        id,
        title,
        startsAtCellId,
        defaultOpen: raw.defaultOpen === true,
        ...(description ? { description } : {}),
    };
}

function statusFromIssues(issues) {
    if (issues.some((entry) => entry.kind === "conflict")) return "conflict";
    if (issues.some((entry) => entry.kind === "unsupported")) return "unsupported";
    return "ready";
}

function runtimeSignature(runtime) {
    return JSON.stringify({
        version: runtime.version,
        controls: runtime.controls,
        cells: runtime.cells,
        bands: runtime.bands,
    });
}

/**
 * Compile the optional Canvas interaction block into one deterministic runtime.
 * The canonical canvas layout remains unchanged; runtime cells attach by the
 * stable kernel UUID already carried by every bundled/current canvas cell.
 */
export function compileCanvasRuntime(raw, rows) {
    if (raw == null) {
        return {
            enabled: false,
            status: "ready",
            issues: [],
            controls: [],
            controlsById: new Map(),
            cells: [],
            cellsById: new Map(),
            cellsByKernelId: new Map(),
            bands: [],
            disclosures: [],
            signature: "disabled",
        };
    }
    const issues = [];
    if (!isRecord(raw)) {
        issues.push(issue("conflict", "invalid-runtime", "Canvas runtime must be an object.", "$runtime"));
    } else {
        reportUnknownKeys(raw, RUNTIME_KEYS, "$runtime", issues);
    }
    const version = raw?.version;
    if (version !== CANVAS_RUNTIME_VERSION) {
        issues.push(issue(
            "unsupported",
            "unsupported-runtime-version",
            `Canvas runtime version ${String(raw?.version ?? "(missing)")} is not supported.`,
            "$runtime.version",
        ));
    }
    const controlValues = Array.isArray(raw?.controls) ? raw.controls : [];
    if (!Array.isArray(raw?.controls) || controlValues.length > MAX_CONTROLS) {
        issues.push(issue(
            "conflict",
            "invalid-controls",
            `Canvas runtime requires an array of at most ${MAX_CONTROLS} controls.`,
            "$runtime.controls",
        ));
    }
    const controls = [];
    const controlsById = new Map();
    controlValues.slice(0, MAX_CONTROLS).forEach((value, index) => {
        const control = normalizeControl(value, `$runtime.controls[${index}]`, issues);
        if (!control) return;
        if (controlsById.has(control.id)) {
            issues.push(issue(
                "conflict",
                "duplicate-control-id",
                `Control id ${control.id} is duplicated.`,
                `$runtime.controls[${index}]`,
            ));
            return;
        }
        controlsById.set(control.id, control);
        controls.push(control);
    });
    const cellValues = Array.isArray(raw?.cells) ? raw.cells : [];
    if (!Array.isArray(raw?.cells) || cellValues.length > MAX_RUNTIME_CELLS) {
        issues.push(issue(
            "conflict",
            "invalid-runtime-cells",
            `Canvas runtime requires an array of at most ${MAX_RUNTIME_CELLS} cells.`,
            "$runtime.cells",
        ));
    }
    const cells = [];
    const cellsById = new Map();
    const cellsByKernelId = new Map();
    cellValues.slice(0, MAX_RUNTIME_CELLS).forEach((value, index) => {
        const cell = normalizeRuntimeCell(value, controlsById, `$runtime.cells[${index}]`, issues);
        if (!cell) return;
        if (cellsById.has(cell.id) || cellsByKernelId.has(cell.kernelId)) {
            issues.push(issue(
                "conflict",
                "duplicate-runtime-cell",
                `Runtime cell ${cell.id} repeats a stable id or kernelId.`,
                `$runtime.cells[${index}]`,
            ));
            return;
        }
        cells.push(cell);
        cellsById.set(cell.id, cell);
        cellsByKernelId.set(cell.kernelId, cell);
    });
    const layoutKernelIds = new Map();
    for (const [rowIndex, row] of (rows || []).entries()) {
        for (const [cellIndex, cell] of (row?.cells || []).entries()) {
            const kernelId = cleanReference(cell?.kernel_id);
            if (!kernelId) {
                issues.push(issue(
                    "unsupported",
                    "unsupported-layout-cell-reference",
                    "Interactive Canvas cells require a stable kernel_id; command-only references are not supported.",
                    `$rows[${rowIndex}].cells[${cellIndex}]`,
                ));
                continue;
            }
            layoutKernelIds.set(kernelId, (layoutKernelIds.get(kernelId) || 0) + 1);
        }
    }
    for (const cell of cells) {
        const count = layoutKernelIds.get(cell.kernelId) || 0;
        if (count !== 1) {
            issues.push(issue(
                "conflict",
                "runtime-cell-layout-mismatch",
                count === 0
                    ? `Runtime cell ${cell.id} does not match a canvas cell.`
                    : `Runtime cell ${cell.id} matches more than one canvas cell.`,
                "$runtime.cells",
            ));
        }
    }
    for (const [kernelId, count] of layoutKernelIds) {
        if (count === 1 && cellsByKernelId.has(kernelId)) continue;
        issues.push(issue(
            "conflict",
            "layout-cell-runtime-mismatch",
            count > 1
                ? `Canvas kernel ${kernelId} appears more than once; interactive cells require unique stable identities.`
                : `Canvas kernel ${kernelId} has no runtime cell declaration.`,
            "$runtime.cells",
        ));
    }
    for (const control of controls) {
        if (!cells.some((cell) => cell.bindings.some((binding) => binding.controlId === control.id))) {
            issues.push(issue(
                "conflict",
                "unbound-control",
                `Control ${control.id} is not bound to any canvas cell.`,
                "$runtime.controls",
            ));
        }
    }
    const bandValues = raw?.bands == null ? [] : raw.bands;
    if (!Array.isArray(bandValues) || bandValues.length > MAX_BANDS) {
        issues.push(issue(
            "conflict",
            "invalid-bands",
            `Canvas runtime supports an array of at most ${MAX_BANDS} bands.`,
            "$runtime.bands",
        ));
    }
    const bands = [];
    const bandIds = new Set();
    const bandStarts = new Set();
    (Array.isArray(bandValues) ? bandValues.slice(0, MAX_BANDS) : []).forEach((value, index) => {
        const band = normalizeBand(value, cellsById, `$runtime.bands[${index}]`, issues);
        if (!band) return;
        if (bandIds.has(band.id) || bandStarts.has(band.startsAtCellId)) {
            issues.push(issue(
                "conflict",
                "duplicate-band",
                `Band ${band.id} repeats a stable id or starting cell.`,
                `$runtime.bands[${index}]`,
            ));
            return;
        }
        bandIds.add(band.id);
        bandStarts.add(band.startsAtCellId);
        bands.push(band);
    });
    for (const row of rows || []) {
        const rowRuntimeIds = (row?.cells || [])
            .map((cell) => cellsByKernelId.get(cell?.kernel_id)?.id)
            .filter(Boolean);
        const starts = bands.filter((band) => rowRuntimeIds.includes(band.startsAtCellId));
        if (starts.length > 1) {
            issues.push(issue(
                "conflict",
                "ambiguous-band-start",
                "Two Canvas bands start in the same row.",
                "$runtime.bands",
            ));
        }
    }
    const disclosures = normalizeDisclosures(raw?.disclosures, "$runtime.disclosures", issues);
    const compiled = {
        enabled: true,
        version,
        status: statusFromIssues(issues),
        issues,
        controls,
        controlsById,
        cells,
        cellsById,
        cellsByKernelId,
        bands,
        disclosures,
    };
    return { ...compiled, signature: runtimeSignature(compiled) };
}

export function defaultCanvasRuntimeState(compiled) {
    return {
        controls: Object.fromEntries(
            (compiled?.controls || []).map((control) => [control.id, control.defaultValue]),
        ),
        openBands: Object.fromEntries(
            (compiled?.bands || []).map((band) => [band.id, band.defaultOpen]),
        ),
    };
}

function storageKey(tenantId, canvasId) {
    if (typeof tenantId !== "string" || typeof canvasId !== "string") return null;
    const tenant = tenantId.trim();
    const canvas = canvasId.trim();
    if (!tenant || !canvas || tenant.length > 256 || canvas.length > 256) return null;
    return `${STORAGE_PREFIX}:${encodeURIComponent(tenant)}:${encodeURIComponent(canvas)}`;
}

function validControlValue(control, value) {
    return control.options.some((option) => sameScalar(option.value, value));
}

export function readCanvasRuntimeState(tenantId, canvasId, compiled) {
    const defaults = defaultCanvasRuntimeState(compiled);
    const key = storageKey(tenantId, canvasId);
    if (!key || compiled?.status !== "ready") return defaults;
    let parsed;
    try {
        const raw = localStorage.getItem(key);
        if (!raw || raw.length > MAX_STORED_CHARS) return defaults;
        parsed = JSON.parse(raw);
    } catch {
        return defaults;
    }
    if (!isRecord(parsed) || parsed.v !== CANVAS_RUNTIME_VERSION) return defaults;
    const controls = { ...defaults.controls };
    if (isRecord(parsed.c)) {
        for (const control of compiled.controls) {
            if (Object.prototype.hasOwnProperty.call(parsed.c, control.id)
                && validControlValue(control, parsed.c[control.id])) {
                controls[control.id] = parsed.c[control.id];
            }
        }
    }
    const openBands = { ...defaults.openBands };
    if (isRecord(parsed.b)) {
        for (const band of compiled.bands) {
            if (typeof parsed.b[band.id] === "boolean") {
                openBands[band.id] = parsed.b[band.id];
            }
        }
    }
    return { controls, openBands };
}

export function writeCanvasRuntimeState(tenantId, canvasId, compiled, state) {
    const key = storageKey(tenantId, canvasId);
    if (!key || compiled?.status !== "ready") return false;
    const controls = {};
    for (const control of compiled.controls) {
        const value = state?.controls?.[control.id];
        if (validControlValue(control, value) && !sameScalar(value, control.defaultValue)) {
            controls[control.id] = value;
        }
    }
    const bands = Object.fromEntries(compiled.bands
        .filter((band) => Boolean(state?.openBands?.[band.id]) !== band.defaultOpen)
        .map((band) => [band.id, Boolean(state?.openBands?.[band.id])]));
    try {
        if (!Object.keys(controls).length && !Object.keys(bands).length) {
            localStorage.removeItem(key);
            return true;
        }
        const serialized = JSON.stringify({
            v: CANVAS_RUNTIME_VERSION,
            ...(Object.keys(controls).length ? { c: controls } : {}),
            ...(Object.keys(bands).length ? { b: bands } : {}),
        });
        if (serialized.length > MAX_STORED_CHARS) return false;
        localStorage.setItem(key, serialized);
        return true;
    } catch {
        return false;
    }
}

export function clearCanvasRuntimeState(tenantId, canvasId) {
    const key = storageKey(tenantId, canvasId);
    if (!key) return false;
    try {
        localStorage.removeItem(key);
        return true;
    } catch {
        return false;
    }
}

function isClearValue(control, value) {
    return control.options.some(
        (option) => option.clear === true && sameScalar(option.value, value),
    );
}

export function buildCanvasCellProjection(compiled, runtimeCell, controlValues, focus) {
    if (!compiled?.enabled || compiled.status !== "ready" || !runtimeCell) {
        return {
            status: compiled?.status || "ready",
            queryParams: {},
            filters: [],
            sort: null,
            focus: null,
            ignoredControls: [],
            issues: compiled?.issues || [],
        };
    }
    const queryParams = {};
    const filters = [];
    let sort = null;
    const boundControls = new Set();
    for (const binding of runtimeCell.bindings) {
        const control = compiled.controlsById.get(binding.controlId);
        const value = validControlValue(control, controlValues?.[control.id])
            ? controlValues[control.id]
            : control.defaultValue;
        boundControls.add(control.id);
        if (binding.kind === "parameter" && !isClearValue(control, value)) {
            queryParams[binding.parameter] = value;
        }
        if (binding.kind === "filter" && !isClearValue(control, value)) {
            filters.push({ field: binding.field, value, controlId: control.id });
        }
        if (binding.kind === "sort") sort = { field: binding.field, direction: value };
    }
    const ignoredControls = compiled.controls.filter((control) => (
        !boundControls.has(control.id)
        && !sameScalar(controlValues?.[control.id], control.defaultValue)
    ));
    const focusBinding = focus?.field
        ? runtimeCell.focus.find((binding) => binding.field === focus.field)
        : null;
    return {
        status: "ready",
        queryParams,
        filters,
        sort,
        focus: focusBinding ? {
            ...focus,
            kind: focusBinding.kind,
            filterRows: focus.sourceCellId !== runtimeCell.id,
        } : null,
        ignoredControls,
        ignoredFocus: Boolean(focus?.field && !focusBinding),
        issues: [],
    };
}

function compareValues(left, right) {
    if (typeof left === "number" && typeof right === "number") return left - right;
    return String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: "base",
    });
}

function rowsHaveField(rows, field) {
    return rows.some((row) => isRecord(row) && Object.prototype.hasOwnProperty.call(row, field));
}

/** Apply post-query bindings without mutating the kernel result. */
export function applyCanvasCellProjection(data, spec, projection) {
    if (!projection || projection.status !== "ready") {
        return {
            status: projection?.status || "conflict",
            data,
            spec,
            issues: projection?.issues || [],
        };
    }
    const operations = [
        ...projection.filters.map((filter) => ({ ...filter, kind: "filter" })),
        ...(projection.focus?.filterRows ? [{ ...projection.focus, kind: "focus" }] : []),
        ...(projection.sort ? [{ ...projection.sort, kind: "sort" }] : []),
    ];
    const nextSpec = projection.focus
        ? {
            ...spec,
            focusField: projection.focus.field,
            focusValue: projection.focus.value,
            runtimeFocus: {
                field: projection.focus.field,
                value: projection.focus.value,
            },
        }
        : spec;
    if (!operations.length) return { status: "ready", data, spec: nextSpec, issues: [] };
    if (!Array.isArray(data)) {
        return {
            status: "schema-unavailable",
            data,
            spec: nextSpec,
            issues: [issue(
                "schema-unavailable",
                "non-tabular-result",
                "This view cannot apply the active Canvas controls because its result is not tabular.",
            )],
        };
    }
    if (!data.length) return { status: "ready", data, spec: nextSpec, issues: [] };
    for (const operation of operations) {
        if (!rowsHaveField(data, operation.field)) {
            return {
                status: "schema-unavailable",
                data,
                spec: nextSpec,
                issues: [issue(
                    "schema-unavailable",
                    "missing-result-field",
                    `This view cannot apply the active Canvas control because field ${operation.field} is unavailable.`,
                )],
            };
        }
    }
    let nextData = data;
    for (const filter of projection.filters) {
        nextData = nextData.filter((row) => sameScalar(row?.[filter.field], filter.value));
    }
    if (projection.focus?.filterRows) {
        nextData = nextData.filter(
            (row) => sameScalar(row?.[projection.focus.field], projection.focus.value),
        );
    }
    if (projection.sort) {
        const direction = projection.sort.direction === "asc" ? 1 : -1;
        nextData = [...nextData].sort((left, right) => {
            const leftValue = left?.[projection.sort.field];
            const rightValue = right?.[projection.sort.field];
            const leftMissing = leftValue == null;
            const rightMissing = rightValue == null;
            if (leftMissing || rightMissing) {
                if (leftMissing && rightMissing) return 0;
                return leftMissing ? 1 : -1;
            }
            return direction * compareValues(leftValue, rightValue);
        });
    }
    return { status: "ready", data: nextData, spec: nextSpec, issues: [] };
}

export function canvasRuntimeStateIsDefault(compiled, state, focus = null) {
    if (focus) return false;
    const defaults = defaultCanvasRuntimeState(compiled);
    return compiled.controls.every((control) => (
        sameScalar(state?.controls?.[control.id], defaults.controls[control.id])
    )) && compiled.bands.every((band) => (
        Boolean(state?.openBands?.[band.id]) === Boolean(defaults.openBands[band.id])
    ));
}

export function groupCanvasRuntimeRows(rows, compiled) {
    if (!compiled?.enabled || compiled.status !== "ready" || !compiled.bands.length) {
        return [{ id: "__unbanded", band: null, rows: rows || [] }];
    }
    const bandByStart = new Map(compiled.bands.map((band) => [band.startsAtCellId, band]));
    const groups = [];
    let current = null;
    for (const row of rows || []) {
        const runtimeIds = (row?.cells || [])
            .map((cell) => compiled.cellsByKernelId.get(cell?.kernel_id)?.id)
            .filter(Boolean);
        const starts = runtimeIds.map((id) => bandByStart.get(id)).filter(Boolean);
        if (starts.length > 1) {
            return [{ id: "__unbanded", band: null, rows: rows || [] }];
        }
        if (starts.length || !current) {
            const band = starts[0] || null;
            current = { id: band?.id || "__unbanded", band, rows: [] };
            groups.push(current);
        }
        current.rows.push(row);
    }
    return groups;
}
